export type MemberShowcaseFunctionSlug =
  | "talent-acquisition"
  | "talent-management-learning"
  | "people-operations"
  | "people-analytics"
  | "workforce-planning"
  | "hr-business-partner";

/** Retired function pages. Keep redirects so old tabs and leftover prerender do not 404. */
export const RETIRED_MEMBER_SHOWCASE_REDIRECTS = {
  "compensation-rewards": "/member-showcase",
  "employee-relations": "/member-showcase",
  assessments: "/member-showcase",
} as const;

export type RetiredMemberShowcaseSlug =
  keyof typeof RETIRED_MEMBER_SHOWCASE_REDIRECTS;

export interface MemberShowcaseCard {
  title: string;
  body: string;
}

export interface MemberShowcaseFunction {
  slug: MemberShowcaseFunctionSlug;
  title: string;
  /** One-sentence role framing for the hub tile and function page intro */
  framing: string;
  cards: MemberShowcaseCard[];
}

export const MEMBER_SHOWCASE_FUNCTIONS: MemberShowcaseFunction[] = [
  {
    slug: "talent-acquisition",
    title: "Talent Acquisition",
    framing:
      "Recruiters who took the paperwork constraint off hiring so they can spend the cycle on the hire.",
    cards: [
      {
        title: "Contingent workforce hiring",
        body: "Every req started as a rebuild for Laurie. In the Mastermind, they built an assistant that assembles the hiring packet, so the team could spend the cycle on the hire, not the paperwork. Time-to-fill came down because they stopped starting from zero.",
      },
      {
        title: "Structured interview questions",
        body: "Jason was tired of writing interview questions from scratch for every role. In the Mastermind, they taught an assistant to read a job description, pull competencies, and draft behavioral and technical questions with strong and weak answers. The first version skipped the rating scale. They put that requirement into the instructions so the next run came back complete.",
      },
      {
        title: "Interview scoring guides",
        body: "Montarious needed a first draft to take into the hiring team, not a finished decision. In the Mastermind, they built an interview guide from job details: criteria, questions, and behaviorally anchored rating scales. The constraint they removed was the blank page by job family. The hiring team still reviews and confirms the guide.",
      },
    ],
  },
  {
    slug: "talent-management-learning",
    title: "Talent Management and L&D",
    framing:
      "Talent and L&D members who made development start faster without handing over the relationship.",
    cards: [
      {
        title: "Career coaching",
        body: "Lindsay wanted coaching that could travel to different audiences without rewriting the science each time. In the Mastermind, they built a core career coach grounded in psychology and behavioral science, then variants for a specific occupation and for people after a layoff. Same evidence base, different people, instead of starting over.",
      },
      {
        title: "Development plans from assessments",
        body: "The blank page after an assessment was slowing the real conversation for Ryan. In the Mastermind, they built an assistant that turns Hogan results into a first-draft development plan, so the manager and employee can argue about choices, not formatting. AI writes the start. They still own the plan.",
      },
      {
        title: "Leadership review forms",
        body: "Review quality was drifting for Vince because every cycle reinvented the form. In the Mastermind, they built an assistant that creates and maintains one shared leadership review structure. The win is consistency they can keep, not more generated prose.",
      },
    ],
  },
  {
    slug: "people-operations",
    title: "People Operations",
    framing:
      "People Ops members who got out from under document piles so judgment has somewhere to land.",
    cards: [
      {
        title: "M&A due diligence",
        body: "Integration due diligence used to bury Sarah and Andrea in documents. In the Mastermind, they built an assistant that reads large HR and acquisition files and flags inconsistencies and gaps, so they walk in with a first pass instead of a pile. People still decide what is material.",
      },
      {
        title: "Startup people operations",
        body: "First-time managers and lean teams did not have a Head of People on call. Anastasia built a practical operating assistant in the Mastermind: policies, people-ops resources, and answers a lean team can use the same day. The expansion is coverage they could not staff, not a theory dump.",
      },
      {
        title: "30-60-90 onboarding plans",
        body: "Generic week-one checklists were the default because personalizing took too long. A Mastermind member built an assistant that drafts 30-60-90 plans from the role and the person's context. The constraint they removed was copy-paste onboarding. The judgment they kept is whether the plan actually fits.",
      },
    ],
  },
  {
    slug: "people-analytics",
    title: "People Analytics",
    framing:
      "Analysts who found a safer, clearer way to see the pattern, then made it usable by others.",
    cards: [
      {
        title: "Synthetic data for people dashboards",
        body: "Carrie needed to design people dashboards without putting real employee files into a model. In the Mastermind, they built a synthetic dataset and mock visualizations first, then rehearsed the story. Responsible use was the design, not an afterthought.",
      },
      {
        title: "360 feedback themes",
        body: "Open-text 360 comments were too much to read well at every layer. A Mastermind member built an assistant that surfaces themes at the individual, team, function, and organization levels, and kept those layers separate on purpose. Collapsing everything into one org score would have hidden the pattern a manager needs.",
      },
      {
        title: "Talent review dashboard",
        body: "An org-health dashboard was too broad for Lo to finish, and they were the only person who could open the file. In the Mastermind, they shipped a narrower talent review dashboard and handed off access so the team could keep using it. The shift was not prettier charts. It was work that no longer depended on one person.",
      },
    ],
  },
  {
    slug: "workforce-planning",
    title: "Workforce Planning",
    framing:
      "Planners who moved the room from a headcount number to the work, the skills, and the rhythm.",
    cards: [
      {
        title: "Headcount planning from job profiles",
        body: "Headcount debates started with a number and skipped the work. Laura built an assistant in the Mastermind that summarizes what a role actually does at a given level, so leaders share language before they argue over count. They expanded the conversation from reqs to work design.",
      },
      {
        title: "Workforce skills inventory",
        body: "Jake needed a skills picture with no extra budget, knowing self-report can be biased and that a perfect inventory was not on the table. In the Mastermind, they built an assistant that asks clarifying questions before it proceeds, then produces a lightweight inventory they can actually use. Naming the constraints first is how they kept the output honest.",
      },
      {
        title: "Standardized planning process",
        body: "Workforce planning was a one-off spreadsheet each cycle. A Mastermind member built an assistant that outlines the process: steps, owners, and what done looks like. They moved from a file they redo to a rhythm they can run.",
      },
    ],
  },
  {
    slug: "hr-business-partner",
    title: "HR Business Partner",
    framing:
      "HRBPs who built shared language for levels and skills before they walk into the talent conversation.",
    cards: [
      {
        title: "Job-level calibration",
        body: "Managers were guessing how adjacent levels differ. Laura built assistants in the Mastermind that explain the work at each level and what a manager role owns, with data protection in the design. HRBPs can now walk into calibration with shared language. The promotion call stays with people.",
      },
      {
        title: "Skills map from job profiles",
        body: "Laura took that further: a skills map from engineering profiles, overlap across levels, and mobility paths. Architecture that used to sit in a folder became something an HRBP can use in a career conversation.",
      },
      {
        title: "Competency model drafts",
        body: "Fuzzy language was blocking everything a Mastermind member wanted to stack on top. They scoped an assistant to draft an enterprise competency model: key skills, proficiency levels, and what is critical upon entry. Getting a shared model on paper expanded what they could do next.",
      },
    ],
  },
];

export const getFunctionBySlug = (
  slug: string,
): MemberShowcaseFunction | undefined =>
  MEMBER_SHOWCASE_FUNCTIONS.find((fn) => fn.slug === slug);

export const getFunctionPath = (slug: string): string =>
  `/member-showcase/${slug}`;

export const MEMBER_SHOWCASE_FUNCTION_PATHS = MEMBER_SHOWCASE_FUNCTIONS.map(
  (fn) => getFunctionPath(fn.slug),
);
